<template>
  <div id="pins-displayer" :class="{ with_side_menu: sideMenuVisible }">
    <SideMenu
      :is-visible="sideMenuVisible"
      :pin="selectedPin"
      :edit-mode="editMode"
      :edit-pin="editPin"
      :pins-url="pinsUrl"
      @close-side-menu="closeSideMenu"
      @delete-pin="deletePin"
      @edit-pin="editPinFromExisting"
      @pin-center="centerPin"
      @pin-created="$emit('pin-created', $event)"
      @pin-selected="selectPin"
      @pin-updated="$emit('pin-updated', $event)"
    />

    <div id="displayer_content">
      <a
        v-if="!editMode"
        id="display_mode_buttons"
        class="box button main-button fixed-movable-button"
        href="#"
        @click.prevent="toggleDisplayMode"
      >
        Changer l'affichage
      </a>
      <a id="new_pin_button" href="#" class="action-button fixed-movable-button" @click.prevent="setCreateMode">+</a>

      <ListDisplayer v-show="displayMode === 'list'" :pins="pins" @pin-selected="selectPin" />
      <MapDisplayer
        v-show="displayMode === 'map'"
        ref="mapDisplayer"
        :pins="pins"
        :edit-mode="editMode"
        :edit-pin="editPin"
        @pin-selected="selectPin"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import ListDisplayer from './ListDisplayer.vue'
import MapDisplayer from './MapDisplayer.vue'
import SideMenu from './SideMenu.vue'

const props = defineProps({
  pins: {
    type: Array,
    required: true,
  },
  pinsUrl: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['pin-created', 'pin-deleted', 'pin-updated'])

const selectedPin = ref(null)
const displayMode = ref('list')
const editMode = ref(false)
const mapDisplayer = ref(null)
const editPin = ref(defaultEmptyPin())

const sideMenuVisible = computed(() => Boolean(selectedPin.value) || editMode.value)

function defaultEmptyPin() {
  return {
    id: null,
    name: '',
    url: '',
    position: { lat: 0, lng: 0 },
    city: '',
    region: '',
    country: '',
    comments: '',
    visited: false,
    to_visit_again: false,
    pin_type: 'default',
    photo: { url: null, remote_url: '' },
  }
}

function refreshMapSoon() {
  nextTick(() => {
    setTimeout(() => mapDisplayer.value?.resizeMap(), 10)
  })
}

function toggleDisplayMode() {
  displayMode.value = displayMode.value === 'list' ? 'map' : 'list'
  if (displayMode.value === 'map') refreshMapSoon()
}

function setEditMode() {
  editMode.value = true
  displayMode.value = 'map'
  refreshMapSoon()
}

function setCreateMode() {
  editPin.value = defaultEmptyPin()
  selectPin(null)
  setEditMode()
}

function selectPin(pin) {
  selectedPin.value = pin
  editMode.value = false
  refreshMapSoon()
}

function closeSideMenu() {
  selectedPin.value = null
  editMode.value = false
  refreshMapSoon()
}

function centerPin(pin) {
  mapDisplayer.value?.centerPin(pin)
}

function editPinFromExisting(pin) {
  editPin.value = {
    id: pin.id,
    name: pin.name,
    url: pin.url,
    position: { lat: pin.position.lat, lng: pin.position.lng },
    city: pin.city,
    region: pin.region,
    country: pin.country,
    visited: pin.visited,
    to_visit_again: pin.to_visit_again,
    pin_type: pin.pin_type,
    comments: pin.comments,
    photo: { url: pin.photo.url, remote_url: '' },
  }
  selectPin(pin)
  setEditMode()
}

async function deletePin(pin) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
  const response = await fetch(pin.url, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': csrfToken,
      Accept: 'application/json',
    },
  })

  if (response.ok) {
    emit('pin-deleted', pin)
    selectPin(null)
  } else {
    console.log(response)
  }
}
</script>
